import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { Link, useParams } from "react-router";
import type { Id } from "@convex/_generated/dataModel";
import { RotateCcw } from "lucide-react";
import { Breadcrumb, Button, PageContainer, PageHeader } from "@repo/ui";
import { dateCourteHeure } from "@repo/utils";
import type { CibleSuppression } from "../../components/ConfirmerSuppression";
import { arianeOrganisation, libelleSection } from "../../lib/ariane";

export function CorbeilleOrganisation() {
  const { id } = useParams<{ id: string }>();
  const organisationId = id as Id<"organisations">;

  const org = useQuery(api.superAdmin.obtenirOrganisation, { organisationId });
  const corbeille = useQuery(api.corbeille.listerCorbeilleOrg, { organisationId });
  const restaurer = useMutation(api.corbeille.restaurer);

  const handleRestaurer = (cible: CibleSuppression) => {
    restaurer({ cible }).catch((err: unknown) =>
      alert(err instanceof Error ? err.message : "Erreur inconnue"),
    );
  };

  if (corbeille === undefined || org === undefined) {
    return (
      <div className="flex justify-center p-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const vide = corbeille.campagnes.length === 0 && corbeille.parcours.length === 0;

  return (
    <PageContainer>
      <Breadcrumb items={arianeOrganisation(organisationId, org?.nom, "corbeille")} />
      <PageHeader
        titre={libelleSection("corbeille")}
        sousTitre="Éléments supprimés, restaurables. Les crédits consommés ne sont ni rendus à la suppression ni refacturés à la restauration."
      />

      {vide && (
        <div className="bg-card border border-border rounded-surface text-center py-12 text-muted-foreground">
          La corbeille est vide.
        </div>
      )}

      {corbeille.campagnes.length > 0 && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-3">Campagnes</h2>
          <div className="bg-card border border-border rounded-surface overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-accent/5 border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Nom</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Candidats</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Supprimée le</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {corbeille.campagnes.map((c) => (
                  <tr key={c._id}>
                    <td className="px-4 py-3">{c.nom}</td>
                    <td className="px-4 py-3">{c.nbCandidats}</td>
                    <td className="px-4 py-3">{dateCourteHeure(c.deletedAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRestaurer({ kind: "campagne", id: c._id })}
                      >
                        <RotateCcw size={12} />
                        Restaurer
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {corbeille.parcours.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold mb-1">Parcours</h2>
          <p className="text-xs text-muted-foreground mb-3">
            Parcours supprimés un par un. Ceux d'une campagne supprimée
            reviennent avec elle.
          </p>
          <div className="bg-card border border-border rounded-surface overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-accent/5 border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Candidat</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Campagne</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Supprimé le</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {corbeille.parcours.map((p) => (
                  <tr key={p._id}>
                    <td className="px-4 py-3">
                      <Link to={`/parcours/${p._id}`} className="hover:underline">
                        {p.candidat}
                      </Link>
                    </td>
                    <td className="px-4 py-3">{p.campagneNom ?? "Hors campagne"}</td>
                    <td className="px-4 py-3">{dateCourteHeure(p.deletedAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRestaurer({ kind: "parcours", id: p._id })}
                      >
                        <RotateCcw size={12} />
                        Restaurer
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </PageContainer>
  );
}
